import { useRequest } from 'ahooks';
import energySavingIcon from '../../images/icon-energySaving@2x.png';
import styles from './index.module.scss';
import { getEnergyAnalysis } from '@/api/energyOverview';
import { formatKgToTonUnit, formatThousandAndUnit, keepOneOrLine } from '@/utils/utils';
import React from 'react';
// 解析带颜色标记的文本
// 格式：#Red文本#Red 表示红色，支持其他颜色如 #Blue, #Green, #Yellow 等
const parseContent = (text: string): React.ReactNode[] => {
  if (!text) return [];

  const parts: React.ReactNode[] = [];
  // 匹配格式：#ColorName文本内容#ColorName
  const regex = /#(\w+)(.*?)#\1/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // 添加匹配前的普通文本
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const colorName = match[1].toLowerCase();
    const coloredText = match[2];

    // 根据颜色名称映射样式
    const colorMap: Record<string, string> = {
      red: '#FF4D4F',
      orange: '#FA8C16',
      yellow: '#FAAD14',
      green: '#52C41A',
      blue: '#1890FF',
      purple: '#722ED1',
      black: '#000000',
      white: '#FFFFFF',
      gray: '#8C8C8C',
      brown: '#A52A2A',
      silver: '#C0C0C0',
      gold: '#FFD700',
    };

    parts.push(
      <span key={match.index} style={{ color: colorMap[colorName] || colorMap.red }}>
        {coloredText}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // 添加最后剩余的普通文本
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // 处理换行符
  return parts.map((part, index) => {
    if (typeof part === 'string') {
      // 将 \n 转换为 <br />
      const lines = part.split('\n');
      return lines.map((line, lineIndex) => (
        <React.Fragment key={`${index}-${lineIndex}`}>
          {line}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return part;
  });
};

const EnergyAnalysis = () => {
  const { data } = useRequest(getEnergyAnalysis);
  return (
    <div className={styles.energyAnalysis}>
      <div className={styles.title}>
        <img src={energySavingIcon} alt="节能降耗" width={15} /> 节能降耗分析
      </div>
      {data?.energySaving && <div className={styles.customText}>{parseContent(data?.energySaving)}</div>}
      <div className={styles.text}>
        本年碳减排
        <span className={styles.value}>
          {formatKgToTonUnit(data?.carbonReduction)?.value}
          {formatKgToTonUnit(data?.carbonReduction).unit}CO2e
        </span>
        ，节约标准煤
        <span className={styles.value}>
          {formatKgToTonUnit(data?.saveStandardCoal)?.value}
          {formatKgToTonUnit(data?.saveStandardCoal)?.unit}ce
        </span>
        ，相当于植树
        <span className={styles.value}>
          {formatThousandAndUnit(data?.treeNum, 1, true).value}
          {formatThousandAndUnit(data?.treeNum).unit}颗
        </span>
        ；本年光伏发电
        <span className={styles.value}>
          {formatThousandAndUnit(data?.pvPower).value}
          {formatThousandAndUnit(data?.pvPower).unit}kWh
        </span>
        ，光伏发电占全厂用电
        <span className={styles.value}>{keepOneOrLine(data?.pvPowerRate)}%</span>，节约电费
        <span className={styles.value}>
          {formatThousandAndUnit(data?.saveElectricity).value}
          {formatThousandAndUnit(data?.saveElectricity).unit}元
        </span>
      </div>
    </div>
  );
};
export default EnergyAnalysis;
